import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import {
  customObjectsService,
  CustomObjectType,
  CustomFieldDef,
  MediaItem,
  MAX_MEDIA_ITEMS_PER_FIELD,
} from '@/services/custom-objects.service';
import { uploadsService } from '@/services/uploads.service';
import { Loader2, X, Film, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Select } from '@/components/Select';

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

function defaultValueFor(f: CustomFieldDef): unknown {
  switch (f.type) {
    case 'boolean': return false;
    case 'multiselect': return [];
    case 'media': return [];
    default: return '';
  }
}

interface DynamicFieldProps {
  field: CustomFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}

function DynamicField({ field, value, onChange }: DynamicFieldProps) {
  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          rows={3}
          className={clsx(inputCls, 'resize-none')}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
    case 'number':
    case 'money':
      return (
        <input
          type="number"
          className={inputCls}
          value={value === '' || value === undefined ? '' : Number(value)}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          className={inputCls}
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'datetime':
      return (
        <input
          type="datetime-local"
          className={inputCls}
          value={typeof value === 'string' ? value.slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'boolean':
      return (
        <label className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="accent-primary"
          />
          {value ? 'Yes' : 'No'}
        </label>
      );
    case 'select':
      return (
        <Select
          className={inputCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— select —</option>
          {(field.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </Select>
      );
    case 'multiselect': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5 rounded-xl border border-border bg-surface p-2">
          {(field.options ?? []).map((opt) => {
            const active = arr.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(active ? arr.filter((x) => x !== opt) : [...arr, opt])}
                className={clsx(
                  'rounded-full border px-2.5 py-1 text-xs',
                  active
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-secondary hover:border-primary/40',
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }
    case 'email':
      return <input type="email" className={inputCls} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case 'phone':
      return <input type="tel" className={inputCls} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case 'url':
      return <input type="url" className={inputCls} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case 'media':
      return (
        <MediaGalleryField
          items={Array.isArray(value) ? (value as MediaItem[]) : []}
          onChange={(next) => onChange(next)}
        />
      );
    default:
      return (
        <input
          type="text"
          className={inputCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
  }
}

/**
 * Inline gallery uploader for `media`-type custom fields. Holds up to
 * MAX_MEDIA_ITEMS_PER_FIELD items, mixed images + videos, all stored on
 * ImageKit via uploadsService. Designed for "service portfolio" style use —
 * salon services, restaurant menu items, real-estate listings, travel
 * packages, etc. — where the operator wants to attach a handful of photos
 * (and occasionally a short video) without dragging in a heavyweight
 * media-library component.
 */
function MediaGalleryField({
  items,
  onChange,
}: {
  items: MediaItem[];
  onChange: (next: MediaItem[]) => void;
}) {
  const imgInputRef = useRef<HTMLInputElement>(null);
  const vidInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const atCap = items.length >= MAX_MEDIA_ITEMS_PER_FIELD;
  const remaining = MAX_MEDIA_ITEMS_PER_FIELD - items.length;

  const handleFiles = async (files: FileList | null, kind: 'image' | 'video') => {
    if (!files || files.length === 0) return;
    const slots = MAX_MEDIA_ITEMS_PER_FIELD - items.length;
    if (slots <= 0) {
      toast.error(`Max ${MAX_MEDIA_ITEMS_PER_FIELD} items per field`);
      return;
    }
    const list = Array.from(files).slice(0, slots);
    if (files.length > slots) {
      toast(`Only ${slots} more slot${slots === 1 ? '' : 's'} available — uploading the first ${slots}.`);
    }
    setUploading(true);
    const next: MediaItem[] = [...items];
    try {
      // Sequential upload — keeps the progress UX simple and avoids hitting
      // ImageKit rate limits on a 10-file burst.
      for (const f of list) {
        try {
          const r = await uploadsService.imagekit(f, 'custom-objects');
          next.push({ kind, url: r.url, fileName: r.name });
        } catch (e: any) {
          toast.error(`${f.name}: ${e?.message ?? 'upload failed'}`);
        }
      }
      onChange(next);
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {items.map((item, idx) => (
            <div
              key={`${item.url}-${idx}`}
              className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-surface-alt"
            >
              {item.kind === 'image' ? (
                <img
                  src={item.thumbnailUrl ?? item.url}
                  alt={item.fileName ?? ''}
                  className="h-full w-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-black/80 text-white">
                  <Film size={28} />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-danger"
                aria-label="Remove"
              >
                <X size={12} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1.5 py-0.5 text-2xs text-white opacity-0 group-hover:opacity-100">
                {item.fileName ?? item.kind}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={imgInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => { void handleFiles(e.target.files, 'image'); e.target.value = ''; }}
        />
        <input
          ref={vidInputRef}
          type="file"
          accept="video/mp4,video/3gpp,video/quicktime"
          className="hidden"
          onChange={(e) => { void handleFiles(e.target.files, 'video'); e.target.value = ''; }}
        />
        <button
          type="button"
          onClick={() => imgInputRef.current?.click()}
          disabled={atCap || uploading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
          Add photo{remaining > 1 ? 's' : ''}
        </button>
        <button
          type="button"
          onClick={() => vidInputRef.current?.click()}
          disabled={atCap || uploading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Film size={12} />}
          Add video
        </button>
        <div className="text-2xs text-text-muted ml-1">
          {items.length}/{MAX_MEDIA_ITEMS_PER_FIELD}
          {atCap && <span className="ml-1 text-danger">(at cap)</span>}
        </div>
      </div>
    </div>
  );
}

export default function CustomObjectRecordEditor() {
  const { typeKey, recordUid } = useParams<{ typeKey: string; recordUid?: string }>();
  const isNew = !recordUid || recordUid === 'new';
  const [searchParams] = useSearchParams();
  const presetContactUid = searchParams.get('contactUid') ?? undefined;
  const presetLeadUid = searchParams.get('leadUid') ?? undefined;
  const nav = useNavigate();
  const [type, setType] = useState<CustomObjectType | null>(null);
  const [title, setTitle] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [linkedContactUid, setLinkedContactUid] = useState<string | undefined>(undefined);
  const [linkedLeadUid, setLinkedLeadUid] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!typeKey) return;
    setLoading(true);
    Promise.all([
      customObjectsService.getType(typeKey),
      isNew ? Promise.resolve(null) : customObjectsService.getRecord(recordUid!),
    ])
      .then(([t, r]) => {
        setType(t);
        if (r) {
          setTitle(r.title ?? '');
          setValues(r.values ?? {});
          setLinkedContactUid(r.contactUid);
          setLinkedLeadUid(r.leadUid);
        } else {
          // Initialise blank values for each field
          const initial: Record<string, unknown> = {};
          for (const f of t.fields) initial[f.key] = defaultValueFor(f);
          setValues(initial);
          // Pre-link to contact / lead if URL provided one (e.g. coming from
          // the Contact detail "+ New record" picker).
          setLinkedContactUid(presetContactUid);
          setLinkedLeadUid(presetLeadUid);
        }
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, recordUid, isNew]);

  const onSave = async () => {
    if (!type) return;
    setSaving(true);
    try {
      // Strip empty strings to undefined to keep backend validation clean
      const cleanValues: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== '' && v !== undefined && v !== null) cleanValues[k] = v;
      }

      if (isNew) {
        const r = await customObjectsService.createRecord({
          typeKey: type.key,
          title: title || undefined,
          contactUid: linkedContactUid,
          leadUid: linkedLeadUid,
          values: cleanValues,
        });
        toast.success('Created');
        // Bounce back to the contact if we came from there, otherwise the records list.
        if (presetContactUid) {
          nav(`/contacts/${presetContactUid}`);
        } else {
          nav(`/custom-objects/${type.key}/${r.recordUid}`);
        }
      } else {
        await customObjectsService.updateRecord(recordUid!, {
          title: title || undefined,
          values: cleanValues,
        });
        toast.success('Saved');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}</div>;
  }

  if (!type) return null;

  const orderedFields = [...type.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      <Topbar
        title={isNew ? `New ${type.label}` : (title || `Edit ${type.label}`)}
        breadcrumb={type.pluralLabel ?? type.label}
      />

      <div className="px-7 py-7 w-full space-y-6">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-2xl border border-border bg-surface/90 px-5 py-3 shadow-soft backdrop-blur-sm">
          <div>
            <div className="text-sm font-semibold text-text-primary">{isNew ? `New ${type.label}` : 'Edit record'}</div>
            <div className="text-xs text-text-muted">All fields marked with * are required.</div>
          </div>
          <Button size="sm" onClick={onSave} loading={saving}>{isNew ? 'Create' : 'Save'}</Button>
        </div>

        <Card padded>
          <SectionHeader title="Details" />
          <FormField label="Title" hint="Display title for this record (auto-derived if blank)">
            <input
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A short title for this record"
            />
          </FormField>

          {orderedFields.map((f) => (
            <FormField
              key={f.key}
              label={f.label}
              required={f.required}
              hint={f.description}
            >
              <DynamicField
                field={f}
                value={values[f.key] ?? defaultValueFor(f)}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
              />
            </FormField>
          ))}
        </Card>
      </div>
    </>
  );
}
